import { ReactNode, useCallback, useState } from 'react'

// Single source of truth for the one-command deploy invocation.
// Anywhere on the site that mentions "one command" or shows the slash command
// must import this constant — never duplicate the string literal.
export const ONE_CMD = '/opendeploy'

function fallbackCopy(text: string) {
  const t = document.createElement('textarea')
  t.value = text
  t.style.position = 'fixed'
  t.style.opacity = '0'
  document.body.appendChild(t)
  t.select()
  try { document.execCommand('copy') } finally { document.body.removeChild(t) }
}

function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text))
  }
  fallbackCopy(text)
  return Promise.resolve()
}

function useCopy() {
  const [copied, setCopied] = useState(false)
  const copy = useCallback(() => {
    void copyText(ONE_CMD).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    })
  }, [])
  return { copied, copy }
}

/**
 * Full chip with terminal prompt + the slash command + an action label.
 * Use as the hero CTA.
 */
export function OneCmdChip() {
  const { copied, copy } = useCopy()
  return (
    <button
      type="button"
      className={`od-why-cmd${copied ? ' is-copied' : ''}`}
      onClick={copy}
      aria-label={copied ? 'Copied the deploy command' : `Copy the deploy command: ${ONE_CMD}`}
    >
      <span className="od-why-cmd-prompt" aria-hidden="true">›</span>
      <code>{ONE_CMD}</code>
      <span className="od-why-cmd-action" aria-hidden="true">
        {copied ? 'copied' : 'click to copy'}
      </span>
    </button>
  )
}

/**
 * Inline pill that wraps prose text (e.g. "one command") and copies the same
 * command on click. Use inside paragraphs where the chip would be too loud.
 */
export function OneCmdInline({ children }: { children: ReactNode }) {
  const { copied, copy } = useCopy()
  return (
    <button
      type="button"
      className={`od-why-cmd-inline${copied ? ' is-copied' : ''}`}
      onClick={copy}
      title={copied ? 'Copied!' : `Click to copy: ${ONE_CMD}`}
      aria-label={copied ? `Copied: ${ONE_CMD}` : `Copy the deploy command: ${ONE_CMD}`}
    >
      {copied ? 'copied' : children}
    </button>
  )
}
